import React, { useCallback } from "react";  
import { useHistory } from "react-router-dom-v5";
import { Button, colors, spacing } from "../library";
import HighFiveSvg from "../library/images/high-five.svg";
import MailSvg from "../library/images/mail.svg";
import { toBRLCurrency } from "../../utils/formatCurrency";
import useCheckoutStep from "../../hooks/useCheckoutStep";

const wrapper = {
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  gap: spacing.l,
  background: colors.white,
  padding: spacing.l,
  width: 350,
  maxWidth: 350,
  borderRadius: spacing.m,
};

const titleStyle = {
  color: colors.black,
  fontWeight: "bold",
  textAlign: "center",
};

const textStyle = {
  color: colors.black,
  fontSize: 12,
  textAlign: "center",
  padding: `0 ${spacing.m}`,
  lineHeight: 1.2,
};

const mailImageStyle = {
  marginRight: spacing.xs,
};

const pendingIconWrapper = {
  width: 64,
  height: 64,
  borderRadius: "50%",
  background: "#FFF3E0",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
};

const pendingSubtitle = {
  color: colors.greyDarker,
  fontSize: 14,
  textAlign: "center",
  lineHeight: 1.5,
  padding: `0 ${spacing.s}`,
  marginTop: `-${spacing.m}`,
};

const detailsCard = {
  width: "100%",
  borderTop: `1px solid ${colors.greyLight}`,
  paddingTop: spacing.m,
};

const detailRow = {
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  marginBottom: spacing.xs,
};

const detailLabel = {
  color: colors.greyDarker,
  fontSize: 14,
};

const detailValue = {
  color: colors.black,
  fontSize: 14,
  fontWeight: 600,
};

const statusBadge = {
  fontSize: 12,
  padding: `${spacing.xxs} ${spacing.xs}`,
  borderRadius: spacing.xxs,
  border: `1px solid #E88D00`,
  color: "#E88D00",
  fontWeight: 500,
};

const upsellHintStyle = {
  color: colors.greyDark,
  fontSize: 12,
  textAlign: "center",
  lineHeight: 1.4,
  marginTop: `-${spacing.m}`,
};

const PENDING_CONFIG = {
  pix: {
    title: "Pagamento em análise",
    description:
      "Pix pode levar alguns instantes até a confirmação. Você receberá um e-mail assim que o pagamento for aprovado.",
    status: "Aguardando pagamento",
  },
  bankslip: {
    title: "Pagamento em análise",
    description:
      "Boleto pode levar até 3 dias úteis para compensação. Você receberá um e-mail assim que o pagamento for confirmado.",
    status: "Aguardando compensação",
  },
};

function ClockIcon() {
  return (
    <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#E88D00" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="10" />
      <polyline points="12 6 12 12 16 14" />
    </svg>
  );
}

function PendingPaymentContent({ paymentMethod, offer, paymentValue, onContinue, user }) {
  const config = PENDING_CONFIG[paymentMethod];
  const courseName = offer?.meta?.course?.name || offer?.name;
  const hasUpsell = offer?.upsellUrl && /^https?:\/\//.test(offer.upsellUrl);

  return (
    <div style={wrapper}>
      <div style={pendingIconWrapper}>
        <ClockIcon />
      </div>
      <h2 style={titleStyle}>{config.title}</h2>
      <p style={pendingSubtitle}>{config.description}</p>
      <div style={detailsCard}>
        <div style={detailRow}>
          <span style={detailLabel}>Curso</span>
          <span style={detailValue}>{courseName}</span>
        </div>
        <div style={detailRow}>
          <span style={detailLabel}>Valor</span>
          <span style={detailValue}>{toBRLCurrency(paymentValue)}</span>
        </div>
        <div style={detailRow}>
          <span style={detailLabel}>Status</span>
          <span style={statusBadge}>{config.status}</span>
        </div>
      </div>
      <Button
        type="button"
        color="black"
        size="big"
        icon={hasUpsell ? undefined : "shopping cart"}
        fluid
        content={hasUpsell ? "Continuar" : "Acessar a plataforma"}
        onClick={onContinue}
      />
      {hasUpsell && (
        <p style={upsellHintStyle}>
          Achamos que você vai se interessar por isso antes de finalizar.
        </p>
      )}
      {!user && (
        <p style={textStyle}>
          <img src={MailSvg} width="14px" alt="mail" style={mailImageStyle} />
          Você receberá um e-mail assim que o pagamento for confirmado com as
          instruções de acesso. Cheque sua caixa postal / Spam
        </p>
      )}
    </div>
  );
}

export default function ThankYouPage({ user, offer, paymentMethod, paymentValue }) {
  const history = useHistory();

  useCheckoutStep("thank-you");

  const handleContinue = useCallback(() => {
    const upsellUrl = offer?.upsellUrl;
    if (upsellUrl && /^https?:\/\//.test(upsellUrl)) {
      window.open(upsellUrl, "_blank");
    }

    if (user) {
      history.push("/");
    } else {
      history.push("/login");
    }
  }, [offer, user, history]);

  const isPending = paymentMethod === "pix" || paymentMethod === "bankslip";
  const hasUpsell = offer?.upsellUrl && /^https?:\/\//.test(offer.upsellUrl);

  if (isPending) {
    return (
      <PendingPaymentContent
        paymentMethod={paymentMethod}
        offer={offer}
        paymentValue={paymentValue}
        onContinue={handleContinue}
        user={user}
      />
    );
  }

  return (
    <div style={wrapper}>
      <h2 style={titleStyle}>
        Parabéns! Compra realizada com sucesso!
      </h2>
      <img src={HighFiveSvg} width="200px" alt="high five" />
      <Button
        type="button"
        color="black"
        size="big"
        icon={hasUpsell ? undefined : "shopping cart"}
        fluid
        content={hasUpsell ? "Continuar" : "Acessar a plataforma"}
        onClick={handleContinue}
      />
      {hasUpsell && (
        <p style={upsellHintStyle}>
          Achamos que você vai se interessar por isso antes de finalizar.
        </p>
      )}
      {!user && (
        <p style={textStyle}>
          <img
            src={MailSvg}
            width="14px"
            alt="mail"
            style={mailImageStyle}
          />
          Você também receberá um email com as instruções de boas vindas.
          Cheque sua caixa postal / Spam
        </p>
      )}
    </div>
  );
}
